import { useMemo, useState } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

function linearRegressionOverIndex(yValues) {
  const n = yValues.length;
  if (n === 0) return { m: 0, b: 0 };
  const ys = yValues.map((v) => (Number.isFinite(Number(v)) ? Number(v) : 0));
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += ys[i];
    sumXY += i * ys[i];
    sumXX += i * i;
  }
  const denom = n * sumXX - sumX * sumX;
  if (Math.abs(denom) < 1e-9) return { m: 0, b: n ? sumY / n : 0 };
  const m = (n * sumXY - sumX * sumY) / denom;
  const b = (sumY - m * sumX) / n;
  return { m, b };
}

function clampToYMax(v, yMax) {
  if (!Number.isFinite(v) || !Number.isFinite(yMax) || yMax <= 0) return 0;
  return Math.min(yMax, Math.max(0, v));
}

function DailyEngagementTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const o = Number(d.open_rate);
  const c = Number(d.click_rate);
  const r = Number(d.reply_rate);
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-[#0A1A2F] mb-1">{d.dateLabelFull}</p>
      <p className="text-gray-700">{Number(d.sends || 0).toLocaleString()} sends</p>
      <p className="text-gray-600 mt-1 tabular-nums">
        {Number.isFinite(o) ? `${o.toFixed(2)}%` : '—'} open ·{' '}
        {Number.isFinite(c) ? `${c.toFixed(2)}%` : '—'} click ·{' '}
        {Number.isFinite(r) ? `${r.toFixed(2)}%` : '—'} reply
      </p>
    </div>
  );
}

/**
 * @param {{ days: Array<{ date: string, sends?: number, human_opens?: number, human_clicks?: number, replies?: number, open_rate?: number, click_rate?: number, reply_rate?: number }>, loading?: boolean }} props
 */
export default function DailyEngagementChart({ days, loading = false }) {
  const [metric, setMetric] = useState('open');

  const { chartData, noSendsInWindow, yMax } = useMemo(() => {
    const raw = Array.isArray(days) ? days : [];
    const maxY =
      metric === 'open' ? 100 : metric === 'reply' ? 10 : 20;
    const rateKey = metric === 'open' ? 'open_rate' : metric === 'click' ? 'click_rate' : 'reply_rate';
    const yVals = raw.map((row) => {
      const v = Number(row?.[rateKey]);
      return Number.isFinite(v) ? v : 0;
    });
    const { m, b } = linearRegressionOverIndex(yVals);
    const mapped = raw.map((row, i) => {
      const dateStr = String(row?.date || '');
      const parsed = dateStr ? parseISO(dateStr) : null;
      const dateLabelFull =
        parsed && isValid(parsed) ? format(parsed, 'MMM d, yyyy') : dateStr || '—';
      const dateLabelShort =
        parsed && isValid(parsed) ? format(parsed, 'MMM d') : dateStr.slice(5) || '—';
      const sends = Number(row?.sends) || 0;
      const openR = Number(row?.open_rate);
      const clickR = Number(row?.click_rate);
      const replyR = Number(row?.reply_rate);
      const rawTrend = m * i + b;
      return {
        dateLabel: dateLabelShort,
        dateLabelFull,
        sends,
        open_rate: Number.isFinite(openR) ? openR : 0,
        click_rate: Number.isFinite(clickR) ? clickR : 0,
        reply_rate: Number.isFinite(replyR) ? replyR : 0,
        rate: yVals[i],
        barValue: Math.min(yVals[i], maxY),
        trend: clampToYMax(rawTrend, maxY),
      };
    });
    const noSends =
      mapped.length === 0 || mapped.every((row) => Number(row.sends) === 0);
    return { chartData: mapped, noSendsInWindow: noSends, yMax: maxY };
  }, [days, metric]);

  const xInterval = chartData.length > 45 ? Math.floor(chartData.length / 10) : chartData.length > 20 ? 2 : 0;

  return (
    <Card className="border-2 border-[#00C2C2]/20 bg-gradient-to-br from-[#00C2C2]/5 to-white h-full">
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <CardTitle className="text-lg text-[#0A1A2F]">Daily engagement</CardTitle>
          <div
            className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 self-start"
            role="group"
            aria-label="Rate metric"
          >
            {[
              { id: 'open', label: 'True open rate' },
              { id: 'click', label: 'True click rate' },
              { id: 'reply', label: 'True reply rate' },
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                className={`rounded-md px-2.5 py-1.5 text-xs sm:text-sm font-medium transition-colors ${
                  metric === id ? 'bg-white text-[#0A1A2F] shadow-sm' : 'text-gray-600 hover:text-[#0A1A2F]'
                }`}
                onClick={() => setMetric(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="h-[280px] bg-gray-100 rounded-md animate-pulse" />
        ) : (
          <div className="relative h-[280px] w-full min-h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 10, fill: '#5C6B7A' }}
                  interval={xInterval}
                  angle={chartData.length > 24 ? -35 : 0}
                  textAnchor={chartData.length > 24 ? 'end' : 'middle'}
                  height={chartData.length > 24 ? 48 : 28}
                />
                <YAxis
                  domain={[0, yMax]}
                  ticks={
                    yMax === 100
                      ? undefined
                      : yMax === 10
                        ? [0, 5, 10]
                        : [0, 5, 10, 15, 20]
                  }
                  tick={{ fontSize: 10, fill: '#5C6B7A' }}
                  width={40}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip content={<DailyEngagementTooltip />} />
                <Bar dataKey="barValue" name="Rate" fill="#00C2C2" radius={[3, 3, 0, 0]} maxBarSize={28} />
                <Line
                  type="monotone"
                  dataKey="trend"
                  name="Trend"
                  stroke="#0A1A2F"
                  strokeWidth={1.75}
                  strokeDasharray="5 4"
                  dot={false}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
            {noSendsInWindow && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-sm font-medium text-gray-600 bg-white/90 px-4 py-2 rounded-md border border-gray-200 shadow-sm">
                  No sends in this window
                </p>
              </div>
            )}
          </div>
        )}
        <p className="text-[10px] text-gray-500 mt-3 leading-snug">
          Today and yesterday will tick up as opens, clicks and replies land.
        </p>
      </CardContent>
    </Card>
  );
}
